import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ShieldCheck,
  Radar,
  Lock,
  Activity,
  KeyRound,
  ShieldHalf,
  BookOpen,
} from "lucide-react";
import { SITE } from "@/lib/site";
import ResourceModal from "@/components/ResourceModal";

const RESOURCES = [
  { key: "entra-id-hardening-checklist", title: "Entra ID Hardening Checklist", icon: KeyRound },
  { key: "az-500-exam-cheatsheet", title: "AZ-500 Cheatsheet", icon: ShieldHalf },
  { key: "cissp-domain-mastery", title: "CISSP Cheatsheet", icon: BookOpen },
];

const PILLARS = ["Microsoft Security", "AI Security", "Threat Hunting", "Cloud Defense"];

const TRUST_CHIPS = [
  "CISSP", "CCSP", "CEH",
  "Microsoft Sentinel", "CrowdStrike Falcon", "Defender XDR", "Splunk",
];

export default function Hero() {
  const [resource, setResource] = useState(null);

  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      <SocBackground />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* ─────────── LEFT ─────────── */}
        <div className="lg:col-span-7" style={{ fontFamily: "Satoshi, sans-serif" }}>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full border border-blue-500/30 bg-blue-500/[0.08] backdrop-blur-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-blue-100">
              Enterprise Security · SOC Transformation
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.08 }}
            className="mt-7 font-display font-bold tracking-tighter leading-[1.02] text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem] text-white"
          >
            Enterprise{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: "linear-gradient(90deg,#67E8F9 0%,#60A5FA 45%,#A5B4FC 100%)",
              }}
            >
              Cloud Security
            </span>{" "}
            &amp; SOC Transformation Specialist.
          </motion.h1>

          {/* Subhead with separators */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.18 }}
            className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-base md:text-lg font-medium text-slate-200"
          >
            {PILLARS.map((p, i) => (
              <span key={p} className="inline-flex items-center gap-2">
                <span className="text-white">{p}</span>
                {i < PILLARS.length - 1 && (
                  <span className="text-blue-400/60" aria-hidden>|</span>
                )}
              </span>
            ))}
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.26 }}
            className="mt-7 max-w-2xl text-base md:text-lg text-slate-400 leading-relaxed"
          >
            Helping organizations modernize security operations through{" "}
            <span className="text-slate-200">Microsoft Security</span>,{" "}
            <span className="text-slate-200">SIEM/SOAR engineering</span>,{" "}
            <span className="text-slate-200">cloud defense</span>,{" "}
            <span className="text-slate-200">AI security readiness</span>, and
            hands-on enterprise training for global teams.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.34 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-book-cta"
              className="group relative inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold tracking-wide overflow-hidden"
              style={{
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                color: "#fff",
                boxShadow:
                  "0 0 0 1px rgba(96,165,250,0.4), 0 10px 40px -8px rgba(37,99,235,0.6), inset 0 1px 0 rgba(255,255,255,0.18)",
              }}
            >
              <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)" }} />
              <span className="relative inline-flex items-center gap-2">
                Book Consultation
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </a>
            <a
              href="#expertise"
              data-testid="hero-programs-cta"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold tracking-wide border border-white/15 text-white hover:border-blue-400/60 hover:bg-white/5 transition-all"
            >
              Explore Expertise
              <span className="text-blue-300">→</span>
            </a>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.42 }}
            className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500"
          >
            <span className="text-slate-600">Trusted stack</span>
            {TRUST_CHIPS.map((t, i) => (
              <span key={t} className="inline-flex items-center gap-3">
                <span className="text-slate-400">{t}</span>
                {i < TRUST_CHIPS.length - 1 && <span className="text-slate-700">·</span>}
              </span>
            ))}
          </motion.div>

          {/* Subtle resources strip */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-7 flex flex-wrap items-center gap-2"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600 mr-1">
              Free briefings ▾
            </span>
            {RESOURCES.map((r) => {
              const Icon = r.icon;
              return (
                <button
                  key={r.key}
                  onClick={() => setResource(r)}
                  data-testid={`hero-resource-cta-${r.key}`}
                  className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 hover:border-blue-400/60 hover:bg-blue-500/5 transition-all text-[12px] text-slate-300 hover:text-white"
                >
                  <Icon size={12} className="text-blue-300" />
                  {r.title}
                </button>
              );
            })}
          </motion.div>
        </div>

        {/* ─────────── RIGHT — SOC operator card ─────────── */}
        <motion.div
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] max-w-md mx-auto">
            {/* Outer glow */}
            <div
              className="absolute -inset-6 rounded-[2rem] blur-2xl opacity-70"
              style={{
                background:
                  "radial-gradient(circle at 30% 20%, rgba(37,99,235,0.35), transparent 60%), radial-gradient(circle at 70% 80%, rgba(6,182,212,0.25), transparent 60%)",
              }}
            />
            {/* Bracket markers */}
            <div className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 rounded-tl-md" style={{ borderColor: "rgba(96,165,250,0.6)" }} />
            <div className="absolute -top-2 -right-2 w-8 h-8 border-t-2 border-r-2 rounded-tr-md" style={{ borderColor: "rgba(96,165,250,0.6)" }} />
            <div className="absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 rounded-bl-md" style={{ borderColor: "rgba(96,165,250,0.6)" }} />
            <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 rounded-br-md" style={{ borderColor: "rgba(96,165,250,0.6)" }} />

            <div
              className="relative h-full w-full overflow-hidden rounded-[1.5rem] border"
              style={{
                borderColor: "rgba(96,165,250,0.30)",
                boxShadow:
                  "0 0 0 1px rgba(59,130,246,0.20), 0 30px 80px -20px rgba(37,99,235,0.45)",
                background: "#070A12",
              }}
            >
              {/* Status header */}
              <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-3 border-b border-blue-400/10" style={{ background: "rgba(7,10,18,0.6)", backdropFilter: "blur(8px)" }}>
                <div className="flex items-center gap-2">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-300">LIVE · Operator Verified</span>
                </div>
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-blue-300">SOC.OPS / 24×7</span>
              </div>

              <img
                src={SITE.portrait}
                alt="Ajay Sahni"
                data-testid="hero-portrait"
                className="h-full w-full object-cover object-top"
                style={{ filter: "saturate(0.92) contrast(1.06)" }}
              />

              {/* Scanline overlay */}
              <div
                className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-50"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(0deg, rgba(96,165,250,0.10) 0px, rgba(96,165,250,0.10) 1px, transparent 1px, transparent 3px)",
                }}
              />
              {/* Sweep beam */}
              <motion.div
                aria-hidden
                initial={{ y: "-30%" }}
                animate={{ y: "130%" }}
                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                className="absolute inset-x-0 h-24 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 0%, rgba(96,165,250,0.25) 50%, transparent 100%)",
                  filter: "blur(2px)",
                }}
              />

              {/* Bottom gradient + nameplate */}
              <div className="absolute inset-x-0 bottom-0 p-5 pt-16" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(7,10,18,0.85) 65%, #070A12 100%)" }}>
                <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-blue-300">aksahni.com</div>
                <div className="mt-1 font-display font-bold text-white text-xl tracking-tight">Ajay Sahni</div>
                <div className="text-xs text-slate-400 mt-0.5">Microsoft / Cybersecurity Strategist · AI Security Mentor</div>
              </div>
            </div>

            {/* Floating SOC tiles */}
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="hidden sm:block absolute -left-6 md:-left-12 top-12 rounded-xl p-3.5 min-w-[150px]"
              style={{
                background: "rgba(10,14,26,0.85)",
                border: "1px solid rgba(96,165,250,0.25)",
                backdropFilter: "blur(14px)",
                boxShadow: "0 10px 30px -10px rgba(0,0,0,0.6)",
              }}
            >
              <div className="flex items-center gap-2">
                <Radar size={12} className="text-blue-300" />
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500">Detections</span>
              </div>
              <div className="mt-1.5 font-display font-bold text-2xl text-white tracking-tighter">
                99.4<span className="text-blue-300">%</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">MITRE ATT&amp;CK coverage</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.75, duration: 0.6 }}
              className="hidden sm:block absolute -right-4 md:-right-12 bottom-24 rounded-xl p-3.5 min-w-[160px]"
              style={{
                background: "rgba(10,14,26,0.85)",
                border: "1px solid rgba(6,182,212,0.30)",
                backdropFilter: "blur(14px)",
                boxShadow: "0 10px 30px -10px rgba(0,0,0,0.6)",
              }}
            >
              <div className="flex items-center gap-2">
                <Activity size={12} className="text-cyan-300" />
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500">MTTR Lift</span>
              </div>
              <div className="mt-1.5 font-display font-bold text-2xl text-white tracking-tighter">
                −60<span className="text-cyan-300">%</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">post-engagement avg.</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="hidden md:block absolute -left-10 -bottom-6 rounded-xl p-3.5 min-w-[180px]"
              style={{
                background: "rgba(10,14,26,0.85)",
                border: "1px solid rgba(96,165,250,0.25)",
                backdropFilter: "blur(14px)",
                boxShadow: "0 10px 30px -10px rgba(0,0,0,0.6)",
              }}
            >
              <div className="flex items-center gap-2">
                <Lock size={12} className="text-blue-300" />
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500">Workshops Run</span>
              </div>
              <div className="mt-1.5 font-display font-bold text-2xl text-white tracking-tighter">
                26<span className="text-blue-300">+</span>
                <span className="ml-2 text-[11px] font-mono font-medium text-slate-500">global cohorts</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <ResourceModal
        open={!!resource}
        onOpenChange={(v) => { if (!v) setResource(null); }}
        resourceKey={resource?.key}
        resourceTitle={resource?.title}
      />
    </section>
  );
}

/* ───────── Animated SOC background ───────── */
function SocBackground() {
  // 7 pulse nodes positioned across the canvas
  const nodes = [
    { x: 12, y: 22, d: 0 },
    { x: 38, y: 14, d: 0.6 },
    { x: 64, y: 30, d: 1.2 },
    { x: 86, y: 12, d: 1.8 },
    { x: 20, y: 70, d: 0.9 },
    { x: 50, y: 78, d: 1.5 },
    { x: 78, y: 68, d: 2.1 },
  ];

  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Radial glows */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 500px at 78% -10%, rgba(37,99,235,0.20), transparent 60%), radial-gradient(700px 400px at 0% 50%, rgba(6,182,212,0.10), transparent 65%), radial-gradient(600px 300px at 50% 110%, rgba(99,102,241,0.10), transparent 70%)",
        }}
      />
      {/* Fine grid */}
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(96,165,250,0.55) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.55) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 90%)",
        }}
      />
      {/* Connection lines (SVG) */}
      <svg
        className="absolute inset-0 w-full h-full opacity-25"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="line" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(96,165,250,0.0)" />
            <stop offset="50%" stopColor="rgba(96,165,250,0.5)" />
            <stop offset="100%" stopColor="rgba(96,165,250,0.0)" />
          </linearGradient>
        </defs>
        <polyline points="12,22 38,14 64,30 86,12" fill="none" stroke="url(#line)" strokeWidth="0.18" vectorEffect="non-scaling-stroke" />
        <polyline points="20,70 50,78 78,68" fill="none" stroke="url(#line)" strokeWidth="0.18" vectorEffect="non-scaling-stroke" />
        <polyline points="38,14 50,78" fill="none" stroke="url(#line)" strokeWidth="0.15" vectorEffect="non-scaling-stroke" />
        <polyline points="64,30 78,68" fill="none" stroke="url(#line)" strokeWidth="0.15" vectorEffect="non-scaling-stroke" />
      </svg>
      {/* Pulse nodes */}
      {nodes.map((n, i) => (
        <span
          key={i}
          className="absolute"
          style={{ left: `${n.x}%`, top: `${n.y}%`, transform: "translate(-50%,-50%)" }}
        >
          <span
            className="block rounded-full"
            style={{
              width: 6, height: 6, background: "#60A5FA",
              boxShadow: "0 0 12px rgba(96,165,250,0.9)",
              animation: `socpulse 3.6s ${n.d}s ease-out infinite`,
            }}
          />
        </span>
      ))}
      {/* Scan beam */}
      <motion.div
        initial={{ x: "-15%" }}
        animate={{ x: "120%" }}
        transition={{ duration: 11, repeat: Infinity, ease: "linear" }}
        className="absolute top-0 bottom-0 w-1/3"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(96,165,250,0.07) 50%, transparent 100%)",
          filter: "blur(8px)",
        }}
      />
      <style>{`
        @keyframes socpulse {
          0%   { transform: scale(1);   opacity: 0.95; }
          70%  { transform: scale(4);   opacity: 0;    }
          100% { transform: scale(4);   opacity: 0;    }
        }
      `}</style>
    </div>
  );
}
